import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Edit,
  Star,
  Check,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useDropzone } from 'react-dropzone';
import { initializeAWS, uploadToS3, generateFileName } from '../utils/awsConfig';
import {
  LIST_DISEASE_IMAGES,
  CREATE_DISEASE_IMAGE,
  UPDATE_DISEASE_IMAGE,
  DELETE_DISEASE_IMAGE,
  UPDATE_DISEASE,
  GET_DISEASES,
} from '../graphql/operations';
import { DiseaseImage, DiseaseImageInput } from '../types';
import { config } from '../config';
import { processImageWithCropData, processImageWithCropArea, optimizeImage, CropData, CropMode } from '../utils/imageProcessing';
import FullPageImageCropper, { CropMode as FullPageCropMode } from './FullPageImageCropper';

interface ImageManagerProps {
  diseaseId: string;
}

interface ImageFormData {
  body_location: string;
  severity: 'MILD' | 'MODERATE' | 'SEVERE' | '';
  fitzpatrick_skin_tone: number | '';
  is_primary: boolean;
}

const ImageManager: React.FC<ImageManagerProps> = ({ diseaseId }) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [showFullPageCropper, setShowFullPageCropper] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<DiseaseImage | null>(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentCropData, setCurrentCropData] = useState<CropData | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(LIST_DISEASE_IMAGES, {
    variables: { disease_id: diseaseId },
  });

  const images = data?.listDiseaseImages?.items || [];

  const [imageFormData, setImageFormData] = useState<ImageFormData>({
    body_location: '',
    severity: '',
    fitzpatrick_skin_tone: '',
    is_primary: false, // Will be updated by useEffect
  });

  useEffect(() => {
    if (!loading && data) {
      setImageFormData(prev => ({
        ...prev,
        is_primary: images.length === 0
      }));
    }
  }, [data, loading, images.length]);

  const [createDiseaseImage] = useMutation(CREATE_DISEASE_IMAGE, {
    onCompleted: (data) => {
      refetch();
      setUploadDialogOpen(false);
      resetForm();
      
      // If this is a primary image, update the disease's thumbnail_url
      if (imageFormData.is_primary && data.createDiseaseImage.thumbnail_url) {
        updateDiseaseThumbnail({
          variables: {
            id: diseaseId,
            input: {
              thumbnail_url: data.createDiseaseImage.thumbnail_url
            }
          }
        });
      }
    },
    onError: (error) => {
      console.error('Error creating disease image:', error);
      setUploadError(error.message);
    },
  });

  const [updateDiseaseImage] = useMutation(UPDATE_DISEASE_IMAGE, {
    onCompleted: (data) => {
      refetch();
      setEditDialogOpen(false);
      setSelectedImage(null);
      resetForm();
      
      // If this is a primary image, update the disease's thumbnail_url
      if (data.updateDiseaseImage.is_primary && data.updateDiseaseImage.thumbnail_url) {
        updateDiseaseThumbnail({
          variables: {
            id: diseaseId,
            input: {
              thumbnail_url: data.updateDiseaseImage.thumbnail_url
            }
          }
        });
      }
    },
    onError: (error) => {
      console.error('Error updating disease image:', error);
    },
  });

  const [deleteDiseaseImage] = useMutation(DELETE_DISEASE_IMAGE, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      console.error('Error deleting disease image:', error);
    },
  });

  const [updateDiseaseThumbnail] = useMutation(UPDATE_DISEASE, {
    update: (cache, { data }, { variables }) => {
      const existingDiseasesQuery = cache.readQuery({ query: GET_DISEASES }) as any;

      if (existingDiseasesQuery && variables) {
        const existingDiseases = existingDiseasesQuery.listDiseases?.items || [];
        const newDiseases = existingDiseases.map((disease: any) => {
          if (disease.id === variables.id) {
            return { ...disease, thumbnail_url: variables.input.thumbnail_url };
          }
          return disease;
        });

        cache.writeQuery({
          query: GET_DISEASES,
          data: {
            listDiseases: {
              ...existingDiseasesQuery.listDiseases,
              items: newDiseases,
            },
          },
        });
      }
    },
    onError: (error) => {
      console.error('Error updating disease thumbnail:', error);
    },
  });

  const resetForm = () => {
    setImageFormData({
      body_location: '',
      severity: '',
      fitzpatrick_skin_tone: '',
      is_primary: images.length === 0, // Default to primary if no existing images
    });
    setUploadError(null);
    setSelectedFile(null);
    setCurrentCropData(null);
    setImagePreviewUrl(null);
  };

  const uploadToS3Local = useCallback(async (file: File, isThumbnail: boolean): Promise<string> => {
    const s3 = await initializeAWS();
    const folder = isThumbnail ? 'thumbnails' : 'conditions';
    
    try {
      return await uploadToS3(file, folder, s3);
    } catch (error) {
      console.error('S3 upload error:', error);
      throw new Error('Failed to upload image to S3');
    }
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file');
      return;
    }

    setProcessing(true);
    setUploadError(null);

    try {
      // First optimize the image if it's too large
      const optimizedFile = await optimizeImage(file);
      
      // Create preview URL for the cropper using the optimized image
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImagePreviewUrl(e.target.result as string);
          setSelectedFile(optimizedFile); // Use optimized file
          setProcessing(false);
          setUploadDialogOpen(false);
          setShowFullPageCropper(true);
        }
      };
      reader.onerror = () => {
        setUploadError('Failed to read optimized image file');
        setProcessing(false);
      };
      reader.readAsDataURL(optimizedFile);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to optimize image');
      setProcessing(false);
    }
  }, []);

  const handleConfirmCrop = useCallback((cropData: any, cropMode: CropMode) => {
    setCurrentCropData({
      x: cropData.x,
      y: cropData.y,
      width: cropData.width,
      height: cropData.height,
      scale: 1, // Full-page cropper works in natural coordinates
      mode: cropMode,
    });
    setShowFullPageCropper(false);
    // Show upload dialog with metadata form after cropping
    setUploadDialogOpen(true);
  }, [selectedFile, diseaseId, imageFormData, createDiseaseImage, currentCropData, uploadToS3Local]);

  const handleConfirmUpload = useCallback(async () => {
    if (!selectedFile || !currentCropData) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Process image with crop area and mode
      const cropArea = { x: currentCropData.x, y: currentCropData.y, width: currentCropData.width, height: currentCropData.height };
      const cropMode = currentCropData.mode || '4:5';
      const { fullImage, thumbnail } = await processImageWithCropArea(selectedFile, cropArea, cropMode);
      
      // Upload both images
      const fullImageUrl = await uploadToS3Local(fullImage, false);
      const thumbnailUrl = await uploadToS3Local(thumbnail, true);
      
      const input: DiseaseImageInput = {
        disease_id: diseaseId,
        image_url: fullImageUrl,
        thumbnail_url: thumbnailUrl,
        body_location: imageFormData.body_location || undefined,
        severity: imageFormData.severity || undefined,
        fitzpatrick_skin_tone: imageFormData.fitzpatrick_skin_tone || undefined,
        is_primary: imageFormData.is_primary,
      };

      await createDiseaseImage({ variables: { input } });
      resetForm();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
      setUploadDialogOpen(true);
    } finally {
      setUploading(false);
    }
  }, [selectedFile, diseaseId, imageFormData, createDiseaseImage, currentCropData]);

  const handleCancelPreview = () => {
    setShowFullPageCropper(false);
    setUploadDialogOpen(true);
    setSelectedFile(null);
    setCurrentCropData(null);
    setImagePreviewUrl(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: false,
  });

  const handleEditClick = (image: DiseaseImage) => {
    setSelectedImage(image);
    setImageFormData({
      body_location: image.body_location || '',
      severity: image.severity || '',
      fitzpatrick_skin_tone: image.fitzpatrick_skin_tone || '',
      is_primary: image.is_primary,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateImage = () => {
    if (!selectedImage) return;

    const input: DiseaseImageInput = {
      disease_id: diseaseId,
      image_url: selectedImage.image_url,
      body_location: imageFormData.body_location || undefined,
      severity: imageFormData.severity || undefined,
      fitzpatrick_skin_tone: imageFormData.fitzpatrick_skin_tone || undefined,
      is_primary: imageFormData.is_primary,
    };

    updateDiseaseImage({ variables: { id: selectedImage.id, input } });
  };

  const handleDeleteImage = async (imageId: string, imageUrl: string, thumbnailUrl?: string, isPrimary?: boolean) => {
    // Prevent multiple confirmations
    if (processing) return;
    
    if (!window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setProcessing(true);
    
    try {
      // Extract S3 keys from URLs
      const extractS3Key = (url: string) => {
        const parts = url.split('/');
        return parts.slice(-2).join('/'); // folder/filename
      };

      const imageKey = extractS3Key(imageUrl);
      const thumbnailKey = thumbnailUrl ? extractS3Key(thumbnailUrl) : null;

      // Delete files from S3
      const deletePromises = [];
      
      if (imageKey) {
        deletePromises.push(
          (await initializeAWS()).deleteObject({
            Bucket: config.aws.s3Bucket,
            Key: imageKey,
          }).promise()
        );
      }
      
      if (thumbnailKey) {
        deletePromises.push(
          (await initializeAWS()).deleteObject({
            Bucket: config.aws.s3Bucket,
            Key: thumbnailKey,
          }).promise()
        );
      }

      await Promise.all(deletePromises);

      // If this was a primary image, clear the disease thumbnail
      if (isPrimary && diseaseId) {
        await updateDiseaseThumbnail({
          variables: {
            id: diseaseId,
            input: {
              thumbnail_url: null,
            },
          },
        });
      }

      // Delete database record
      await deleteDiseaseImage({ variables: { id: imageId } });
      
      console.log('Image deleted successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      {showFullPageCropper && imagePreviewUrl ? (
        <FullPageImageCropper
          imageUrl={imagePreviewUrl}
          onConfirm={handleConfirmCrop}
          onCancel={handleCancelPreview}
        />
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Disease Images</Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
          </Box>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          {loading && <CircularProgress />}

          {error && (
            <Alert severity="error">
              Error loading images: {error.message}
            </Alert>
          )}

          <Grid container spacing={3}>
            {images.map((image: DiseaseImage) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Card>
                  <CardMedia
                    component="img"
                    height={512}
                    image={image.thumbnail_url || image.image_url}
                    alt="Disease image"
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="textSecondary">
                        {image.body_location || 'No location specified'}
                      </Typography>
                      {image.is_primary && <Star color="primary" fontSize="small" />}
                    </Box>
                    
                    {image.severity && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Severity: {image.severity}
                      </Typography>
                    )}
                    
                    {image.fitzpatrick_skin_tone && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Skin Tone: {image.fitzpatrick_skin_tone}
                      </Typography>
                    )}

                    <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(image)}
                        title="Edit"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteImage(image.id, image.image_url, image.thumbnail_url, image.is_primary)}
                        title="Delete"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {images.length === 0 && !loading && (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No images uploaded yet
              </Typography>
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload your first image
              </Button>
            </Box>
          )}

          {/* Upload Dialog */}
          <Dialog
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Upload Disease Image</DialogTitle>
            <DialogContent>
              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}
              
              <Box
                {...(currentCropData ? {} : getRootProps())}
                sx={{
                  border: currentCropData ? '2px solid #4caf50' : '2px dashed #ccc',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: currentCropData ? 'default' : 'pointer',
                  mb: 3,
                  backgroundColor: currentCropData 
                    ? 'rgba(76, 175, 80, 0.1)' 
                    : isDragActive 
                    ? '#f0f0f0' 
                    : 'transparent',
                }}
              >
                {!currentCropData && <input {...getInputProps()} />}
                {processing ? (
                  <CircularProgress />
                ) : uploading ? (
                  <CircularProgress />
                ) : currentCropData ? (
                  <>
                    <Check sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
                    <Typography variant="h6" gutterBottom color="success.main">
                      Image cropped and ready for upload
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedFile?.name}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Fill in the details below and click Upload
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {isDragActive ? 'Drop image here' : 'Drag & drop an image here'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      or click to select a file
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Supported formats: JPEG, PNG, GIF, WebP
                    </Typography>
                  </>
                )}
              </Box>

              {/* Form fields for image metadata */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Body Location"
                    value={imageFormData.body_location}
                    onChange={(e) => setImageFormData(prev => ({ ...prev, body_location: e.target.value }))}
                    placeholder="e.g., Face, Arms, Back, etc."
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={imageFormData.severity}
                      label="Severity"
                      onChange={(e) => setImageFormData(prev => ({ ...prev, severity: e.target.value as any }))}
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="MILD">Mild</MenuItem>
                      <MenuItem value="MODERATE">Moderate</MenuItem>
                      <MenuItem value="SEVERE">Severe</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fitzpatrick Skin Tone</InputLabel>
                    <Select
                      value={imageFormData.fitzpatrick_skin_tone}
                      label="Fitzpatrick Skin Tone"
                      onChange={(e) => setImageFormData(prev => ({ ...prev, fitzpatrick_skin_tone: e.target.value as any }))}
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value={1}>Type I - Very fair</MenuItem>
                      <MenuItem value={2}>Type II - Fair</MenuItem>
                      <MenuItem value={3}>Type III - Medium</MenuItem>
                      <MenuItem value={4}>Type IV - Olive</MenuItem>
                      <MenuItem value={5}>Type V - Brown</MenuItem>
                      <MenuItem value={6}>Type VI - Black</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={imageFormData.is_primary}
                        onChange={(e) => setImageFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
                      />
                    }
                    label="Set as primary image"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
              {currentCropData && imagePreviewUrl && (
                <Button 
                  onClick={() => {
                    setUploadDialogOpen(false);
                    setShowFullPageCropper(true);
                  }}
                  variant="outlined"
                >
                  Re-crop
                </Button>
              )}
              <Button 
                onClick={handleConfirmUpload} 
                variant="contained"
                disabled={uploading || !currentCropData}
                startIcon={uploading ? <CircularProgress size={20} /> : null}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Edit Image Details</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Body Location"
                    value={imageFormData.body_location}
                    onChange={(e) => setImageFormData(prev => ({ ...prev, body_location: e.target.value }))}
                    placeholder="e.g., Face, Arms, Back, etc."
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={imageFormData.severity}
                      label="Severity"
                      onChange={(e) => setImageFormData(prev => ({ ...prev, severity: e.target.value as any }))}
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="MILD">Mild</MenuItem>
                      <MenuItem value="MODERATE">Moderate</MenuItem>
                      <MenuItem value="SEVERE">Severe</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fitzpatrick Skin Tone</InputLabel>
                    <Select
                      value={imageFormData.fitzpatrick_skin_tone}
                      label="Fitzpatrick Skin Tone"
                      onChange={(e) => setImageFormData(prev => ({ ...prev, fitzpatrick_skin_tone: e.target.value as any }))}
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value={1}>Type I - Very fair</MenuItem>
                      <MenuItem value={2}>Type II - Fair</MenuItem>
                      <MenuItem value={3}>Type III - Medium</MenuItem>
                      <MenuItem value={4}>Type IV - Olive</MenuItem>
                      <MenuItem value={5}>Type V - Brown</MenuItem>
                      <MenuItem value={6}>Type VI - Black</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={imageFormData.is_primary}
                        onChange={(e) => setImageFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
                      />
                    }
                    label="Set as primary image"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateImage} variant="contained">
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default ImageManager;
